import type { CompRef } from '@wix/document-services-types';
import { EditorElement } from '@wix/component-protocol/schema';
import { ManifestComponentExtensionType } from './stores';
import { UNEXISTING_COMPONENT_PREFIX } from './builders/StandaloneBuilder';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export enum ChangeType {
  Props = 'props',
  Styles = 'styles',
  Data = 'data',
  Preset = 'preset',
  State = 'state',
  DesignState = 'design-state',
  Manifest = 'manifest',
  Bindings = 'bindings',
  Providers = 'providers',
  Global = 'global',
  GlobalProps = 'global-props',
  GlobalData = 'global-data',
  GlobalStyles = 'global-styles',
  GlobalPreset = 'global-preset',
  GlobalState = 'global-state',
  GlobalBindings = 'global-bindings',
  GlobalProviders = 'global-providers',
  Invalidate = 'invalidate',
}

const GLOBAL_PATCH_TYPES: ChangeType[] = [
  ChangeType.Global,
  ChangeType.GlobalData,
  ChangeType.GlobalStyles,
  ChangeType.GlobalPreset,
  ChangeType.GlobalState,
  ChangeType.GlobalProps,
  ChangeType.GlobalBindings,
  ChangeType.GlobalProviders,
];

export interface IPatch<TType extends ChangeType, TValue> {
  type: TType;
  value: TValue;
  editorPointer: EditorPointer<CompRef>;
  extensionType: ManifestComponentExtensionType;
}

export interface IGlobalPatch {
  type:
    | ChangeType.Global
    | ChangeType.GlobalData
    | ChangeType.GlobalStyles
    | ChangeType.GlobalPreset
    | ChangeType.GlobalState
    | ChangeType.GlobalProps
    | ChangeType.GlobalBindings
    | ChangeType.GlobalProviders;
  value: null;
}

export type IComponentPatch =
  | IPatch<
      ChangeType.Styles,
      {
        cssProperties: Record<string, string | number | undefined>;
        cssCustomProperties: Record<string, string | number | undefined>;
      }
    >
  | IPatch<ChangeType.Data, Record<string, any>>
  | IPatch<ChangeType.Manifest, EditorElement | null>
  | IPatch<ChangeType.Preset, string | null>
  | IPatch<ChangeType.Invalidate, null>
  | IPatch<ChangeType.State, string | null>
  | IPatch<ChangeType.DesignState, string[]>
  | IPatch<ChangeType.Props, Record<string, any>>
  | IPatch<ChangeType.Bindings, null>
  | IPatch<ChangeType.Providers, null>;

export type IChangeCallback = (patch: IComponentPatch | IGlobalPatch) => void;

export enum ListenerPriority {
  External = 0,
  Internal = 1,
}

export interface IManifestChangeDetection {
  subscribe: (
    cb: IChangeCallback,
    options?: {
      editorPointers?: EditorPointer<CompRef>[];
      priority?: ListenerPriority;
    },
  ) => () => void;

  notify: (patch: IComponentPatch) => void;
}

export class NoopManifestChangeDetection implements IManifestChangeDetection {
  subscribe() {
    return () => {};
  }

  notify() {}
}

export class ManifestChangeDetection implements IManifestChangeDetection {
  #pendingPatchTypes = new Set<ChangeType>();
  #queue: (IGlobalPatch | IComponentPatch)[] = [];
  #queueIdx: number | undefined;

  #listeners: {
    callback: IChangeCallback;
    priority: ListenerPriority;
  }[] = [];

  static isComponentPatch(
    patch: IComponentPatch | IGlobalPatch,
  ): patch is IComponentPatch {
    return !GLOBAL_PATCH_TYPES.includes(patch.type);
  }

  static isGlobalPatch(
    patch: IComponentPatch | IGlobalPatch,
  ): patch is IGlobalPatch {
    return GLOBAL_PATCH_TYPES.includes(patch.type);
  }

  /**
   * maybe instead of comparing breadcrumbs, we can
   * use a root element key for checking
   */
  static isPatchMatchEditorPointers(
    editorPointerAPI: EditorPointerServiceAPI,
    patch: IComponentPatch | IGlobalPatch,
    editorPointers?: EditorPointer<CompRef>[],
  ) {
    if (!editorPointers?.length) {
      return false;
    }

    if (ManifestChangeDetection.isGlobalPatch(patch)) {
      return true;
    }

    /**
     * For manifest/invalidate changes, match by root editor pointer,
     * ignoring inner element path,
     * because we do not notify on inner element manifest change (at DynamicBehaviour).
     *
     * For state/preset changes, also match by root editor pointer,
     * ignoring inner element path,
     * because state and preset are set at the component level
     * and affect all inner elements.
     */
    if (
      patch.type === ChangeType.Manifest ||
      patch.type === ChangeType.Invalidate ||
      patch.type === ChangeType.State ||
      patch.type === ChangeType.DesignState ||
      patch.type === ChangeType.Preset
    ) {
      const keys = editorPointers
        .map((x) =>
          editorPointerAPI.buildEditorPointer(
            editorPointerAPI.getDocumentPointer(x),
          ),
        )
        .map((x) => x.asUniqueKey);

      const patchEditorPointer = editorPointerAPI.buildEditorPointer(
        editorPointerAPI.getDocumentPointer(patch.editorPointer),
      );

      return keys.includes(patchEditorPointer.asUniqueKey);
    }

    const keys = editorPointers.map((x) => x.asUniqueKey);

    const patchEditorPointer = patch.editorPointer;
    const patchDocumentPointer =
      editorPointerAPI.getDocumentPointer(patchEditorPointer);

    /**
     * if the patch editor pointer's inner elements path is [a, b, c]
     * we compare all levels
     * [a, b, c]
     * [a, b]
     * [a]
     * []
     */
    const patchEditorPointerInnerElementPath =
      editorPointerAPI.getInnerElementPath(patchEditorPointer);

    const editorPointersBreadcrumbs = patchEditorPointerInnerElementPath?.length
      ? patchEditorPointerInnerElementPath.reduce(
          (acc, childKey) => {
            const currentEditorPointer = acc[acc.length - 1];

            const documentPointer =
              editorPointerAPI.getDocumentPointer(currentEditorPointer);
            const innerElementPath =
              editorPointerAPI.getInnerElementPath(currentEditorPointer) ?? [];

            return [
              ...acc,
              editorPointerAPI.buildEditorPointer(documentPointer, [
                ...innerElementPath,
                childKey,
              ]),
            ];
          },
          [editorPointerAPI.buildEditorPointer(patchDocumentPointer)],
        )
      : [patchEditorPointer];

    const editorPointersBreadcrumbsKeys = editorPointersBreadcrumbs.map(
      (x) => x.asUniqueKey,
    );

    /**
     * editorPointers: { path: [] }[]
     * patch { path: ['child', 'item'] }
     *    ->
     *     { path: ['child', 'item'] }
     *     { path: ['child'] }
     *     { path: [] }
     *
     */
    return keys.some((x) => editorPointersBreadcrumbsKeys.includes(x));
  }

  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    public readonly isInTransaction: () => boolean,
    private readonly registerToSiteChanged: (
      cb: (tags: string[]) => void,
    ) => void,
  ) {
    /**
     * TODO: check inner elements
     */
    this.registerToSiteChanged((tags) => {
      if (!tags?.length) {
        return;
      }

      const match = (_tags: string[]) => _tags.some((x) => tags.includes(x));

      if (
        match([
          'components.presets.set',
          'components.innerElements.presets.set',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalPreset,
          value: null,
        });
      }

      if (
        match([
          'components.runtime.updateProps',
          'components.runtime.setProps',
          'components.runtime.removeProps',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalProps,
          value: null,
        });
      }

      if (
        match([
          // OOI set style
          'tpa.style.setCompStyleParam',
          'components.style.update',
          // Builder set styles
          'components.cssStyle.update',
          'components.cssStyle.remove',
          'components.innerElements.cssStyle.update',
          'components.innerElements.cssStyle.remove',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalStyles,
          value: null,
        });
      }

      if (
        match([
          'tpa.data.set', // OOI set data
          'tpa.data.setMultiple',
          'components.data.set',
          'components.data.update',
          'components.data.remove',
          'components.innerElements.data.set',
          'components.innerElements.data.update',
          'components.innerElements.data.remove',
          'menu.update',
          'menu.removeItem',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalData,
          value: null,
        });
      }

      if (
        match([
          'bindings.bind',
          'bindings.bindAll',
          'bindings.unbind',
          'bindings.unbindProperties',
          'bindings.functions.bind',
          'bindings.templates.bind',
          'components.innerElements.bindings.bind',
          'components.innerElements.bindings.bindAll',
          'components.innerElements.bindings.unbind',
          'components.innerElements.bindings.unbindProperties',
          'components.innerElements.bindings.functions.bind',
          'components.innerElements.bindings.templates.bind',
          'components.runtimeBindings.bind',
          'components.runtimeBindings.unbind',
          'components.runtimeBindings.unbindExtensionModes',
          'components.runtimeBindings.functions.bind',
          'components.runtimeBindings.templates.bind',
          'components.innerElements.runtimeBindings.bind',
          'components.innerElements.runtimeBindings.unbind',
          'components.innerElements.runtimeBindings.unbindExtensionModes',
          'components.innerElements.runtimeBindings.functions.bind',
          'components.innerElements.runtimeBindings.templates.bind',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalBindings,
          value: null,
        });
      }

      if (
        match([
          'contexts.attach',
          'contexts.detach',
          'contexts.data.update',
          'contexts.data.remove',
          'contexts.data.removeProperties',
        ])
      ) {
        this.#notify({
          type: ChangeType.GlobalProviders,
          value: null,
        });
      }

      if (
        match([
          'transactionFromOtherClient',
          'history.undo',
          'history.redo',
          'importExport.components.core.replace',
          'importExport.components.core.add',
          'importExport.pages.core.add',
          'importExport.pages.core.replace',
          'importExport.components.paranative.replace',
          'importExport.components.paranative.add',
          'importExport.pages.paranative.add',
          'importExport.pages.paranative.replace',
          /**
           * TODO: when dm api calls are wrapped with transaction
           * we do not know which api were called
           */
          // 'transaction'
        ])
      ) {
        this.#notify({
          type: ChangeType.Global,
          value: null,
        });
      }
      /**
       * I am not sure if this is a correct statement,
       * but the idea is as soon as transaction is happened (we tbh do not know what happens inside transaction)
       * we just flush the patch queue
       */
      if (match(['transaction'])) {
        if (this.#queue.length > 0) {
          this.#flushQueue();
        } else {
          [
            ChangeType.GlobalStyles,
            ChangeType.GlobalData,
            ChangeType.GlobalPreset,
          ].forEach((type) => {
            this.#notify({
              type,
              value: null,
            } as IGlobalPatch);
          });
        }
      }
    });
  }

  subscribe(
    cb: IChangeCallback,
    options: {
      editorPointers?: EditorPointer<CompRef>[];
      priority?: ListenerPriority;
    } = {},
  ) {
    // use local callback variable as reference object to delete listener
    const callback: IChangeCallback = (
      patch: IComponentPatch | IGlobalPatch,
    ) => {
      const shouldTriggerCallback = options.editorPointers?.length
        ? ManifestChangeDetection.isPatchMatchEditorPointers(
            this.editorPointerAPI,
            patch,
            options.editorPointers,
          )
        : true;

      if (shouldTriggerCallback) {
        cb(patch);
      }
    };

    this.#listeners.push({
      callback,
      priority: options.priority ?? ListenerPriority.Internal,
    });

    return () => {
      const index = this.#listeners.findIndex((x) => x.callback === callback);

      if (index !== -1) {
        this.#listeners.splice(index, 1);
      }
    };
  }

  notify(patch: IComponentPatch) {
    if (
      patch?.editorPointer.asUniqueKey.includes(UNEXISTING_COMPONENT_PREFIX)
    ) {
      return;
    }

    this.#queue.push(patch);
    this.#pendingPatchTypes.add(patch.type);

    const isInTransaction = this.isInTransaction();

    if (
      isInTransaction ||
      patch.type === ChangeType.Manifest ||
      patch.type === ChangeType.Invalidate ||
      patch.type === ChangeType.State ||
      patch.type === ChangeType.DesignState
    ) {
      this.#flushQueue({
        immediate: isInTransaction,
      });
    }
  }

  #notify(patch: IGlobalPatch) {
    this.#queue.push(patch);
    this.#pendingPatchTypes.add(patch.type);
    this.#flushQueue();
  }

  #getRelatedTypes(type: ChangeType): ChangeType[] {
    switch (type) {
      case ChangeType.GlobalData:
        return [ChangeType.GlobalData, ChangeType.Data];
      case ChangeType.GlobalStyles:
        return [ChangeType.GlobalStyles, ChangeType.Styles];
      case ChangeType.GlobalPreset:
        return [ChangeType.GlobalPreset, ChangeType.Preset];
      case ChangeType.GlobalState:
        return [
          ChangeType.GlobalState,
          ChangeType.State,
          ChangeType.DesignState,
        ];
      case ChangeType.GlobalProps:
        return [ChangeType.GlobalProps, ChangeType.Props];
      case ChangeType.GlobalBindings:
        return [ChangeType.GlobalBindings, ChangeType.Bindings];
      case ChangeType.GlobalProviders:
        return [ChangeType.GlobalProviders, ChangeType.Providers];
      default:
        return [type];
    }
  }

  #flushQueue({ immediate }: { immediate?: boolean } = { immediate: false }) {
    clearTimeout(this.#queueIdx);

    const flush = () => {
      const typesToFlush = new Set<ChangeType>();

      this.#pendingPatchTypes.forEach((type) => {
        this.#getRelatedTypes(type).forEach((t) => typesToFlush.add(t));
      });

      this.#pendingPatchTypes.clear();

      const shouldFlushAll =
        typesToFlush.has(ChangeType.Global) ||
        typesToFlush.has(ChangeType.Invalidate);

      let queue: (IGlobalPatch | IComponentPatch)[] = [];
      if (shouldFlushAll) {
        queue = [...this.#queue];
        this.#queue = [];
      } else {
        queue = this.#queue.filter((patch) => typesToFlush.has(patch.type));
        this.#queue = this.#queue.filter(
          (patch) => !typesToFlush.has(patch.type),
        );
      }

      const hasGlobal = queue.some((x) => x.type === ChangeType.Global);
      const hasData = queue.some((x) => x.type === ChangeType.Data);
      const hasStyles = queue.some((x) => x.type === ChangeType.Styles);
      const hasPreset = queue.some((x) => x.type === ChangeType.Preset);
      const hasState = queue.some((x) => x.type === ChangeType.State);
      const hasProps = queue.some((x) => x.type === ChangeType.Props);
      const hasBindings = queue.some((x) => x.type === ChangeType.Bindings);
      const hasProviders = queue.some((x) => x.type === ChangeType.Providers);

      const filteredQueue = hasGlobal
        ? [queue.find((x) => x.type === ChangeType.Global)!]
        : queue.filter((x) => {
            if (x.type === ChangeType.GlobalData && hasData) {
              return false;
            }

            if (x.type === ChangeType.GlobalStyles && hasStyles) {
              return false;
            }

            if (x.type === ChangeType.GlobalPreset && hasPreset) {
              return false;
            }

            if (x.type === ChangeType.GlobalState && hasState) {
              return false;
            }

            if (x.type === ChangeType.GlobalProps && hasProps) {
              return false;
            }

            if (x.type === ChangeType.GlobalBindings && hasBindings) {
              return false;
            }

            if (x.type === ChangeType.GlobalProviders && hasProviders) {
              return false;
            }

            return true;
          });

      const finalQueue = this.#groupAndMergeQueue(filteredQueue);

      const listeners = [...this.#listeners]
        .sort((a, b) => b.priority - a.priority)
        .filter((x) => {
          /**
           * fix for maximum call stack issues,
           * in case of immediate notification, we notify only indent listeners
           * and never external
           *
           * note that probably it may lead to some UI unsync bugs
           */
          if (immediate) {
            return x.priority === ListenerPriority.Internal;
          }

          return x;
        });

      /**
       * TODO: we need to call internal listeners before externals
       */
      finalQueue.forEach((patch) => {
        listeners.forEach((listener) => {
          listener.callback(patch);
        });
      });
    };

    if (immediate) {
      flush();
    } else {
      this.#queueIdx = setTimeout(() => {
        flush();
      }, 16.67) as unknown as number;
    }
  }

  #groupAndMergeQueue(
    queue: (IGlobalPatch | IComponentPatch)[],
  ): (IGlobalPatch | IComponentPatch)[] {
    const grouped = new Map<
      string,
      {
        type: ChangeType;
        patches: (IComponentPatch | IGlobalPatch)[];
      }
    >();

    queue.forEach((patch) => {
      const key =
        'editorPointer' in patch
          ? `${patch.editorPointer.asUniqueKey}:${patch.type}`
          : patch.type;

      if (!grouped.has(key)) {
        grouped.set(key, {
          type: patch.type,
          patches: [],
        });
      }

      grouped.get(key)!.patches.push(patch);
    });

    const result: (IGlobalPatch | IComponentPatch)[] = [];

    grouped.forEach(({ type, patches }) => {
      if (patches.length === 1) {
        result.push(patches[0]);
        return;
      }

      if (type === ChangeType.Data || type === ChangeType.Styles) {
        const allNull = patches.every((p) => p.value === null);

        let mergedValue: Extract<
          IComponentPatch,
          {
            type: ChangeType.Styles | ChangeType.Data;
          }
        >['value'] = {};

        if (!allNull) {
          mergedValue = {};

          patches
            .filter((patch) => {
              return patch.value !== null && typeof patch.value === 'object';
            })
            .forEach((patch) => {
              const value = (patch.value ?? {}) as Record<string, any>;

              if (type === ChangeType.Styles) {
                mergedValue = {
                  cssProperties: {
                    ...mergedValue?.cssProperties,
                    ...value?.cssProperties,
                  },
                  cssCustomProperties: {
                    ...mergedValue?.cssCustomProperties,
                    ...value?.cssCustomProperties,
                  },
                };
              } else {
                mergedValue = { ...mergedValue, ...value };
              }
            });
        }

        result.push({
          ...patches[0],
          value: mergedValue,
        } as IComponentPatch);
      } else {
        result.push(patches[patches.length - 1]);
      }
    });

    return result;
  }

  /**
   * for dev tool purpose only
   */
  notifyGlobalPatch() {
    this.#notify({
      type: ChangeType.Global,
      value: null,
    });
  }

  notifyGlobalStatePatch() {
    this.#notify({
      type: ChangeType.GlobalState,
      value: null,
    });
  }

  cleanup(editorPointers: EditorPointer<CompRef>[]) {
    // should be called when hosts are not in use
    // see FinalizationRegistry in PlatformSDKHost
    // cleanup hosts, contexts, caches, factories by editor pointer
  }
}
